import ContentLoader from 'react-content-loader';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import { isFreeTier } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import Button from '../../generic/Button';
import FetcherIcon from '../../generic/FetcherIcon';
import Flex from '../../generic/Flex';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import QualityRequired from '../../generic/Gates/QualityRequired';
import { icons } from '../../generic/Icon';
import List from '../../generic/List';
import PackageLabelsList from '../../shared/packageLabels/PackageLabelsList';
import type { PackageLabelAssignmentWithName } from '../../../../shared/model-typings/packagelabelassignment';

import PackageDetailsMetadataDescription from './PackageDetailsMetadataDescription';

import styles from './PackageDetailsMetadata.module.less';

//  Displays a placeholder loading UI that looks like package metadata.
const PackageInventoryMetadataLoading = () => (
  <div
    className="package-inventory__details__metadata package-inventory__metadata--loading"
    data-test-id="package-inventory-loading-metadata"
  >
    <ContentLoader width="100%" height="105px">
      <rect x="0" y="0" rx="3" ry="3" width="35%" height="30" />
      <rect x="0" y="50" rx="3" ry="3" width="45%" height="15" />
      <rect x="0" y="80" rx="3" ry="3" width="10%" height="20" />
      <rect x="13%" y="80" rx="3" ry="3" width="20%" height="20" />
      34
      <rect x="36%" y="80" rx="3" ry="3" width="20%" height="20" />
    </ContentLoader>
  </div>
);

type Props = {
  onShowBlockAllModal: () => void;
  onShowManageLabelsModal: () => void;
};

/**
 * This component renders the metadata about a package and its usage in the org. The data here does take into account
 * selected filters.
 */
const PackageDetailsMetadata: VoidFunctionComponent<Props> = ({ onShowBlockAllModal, onShowManageLabelsModal }) => {
  const isLoading = useSelector(PackageInventoryStore.selectors.getPackageMetaDataLoading);
  const packageMetadata = useSelector(PackageInventoryStore.selectors.getPackageMetadata);
  const packageDetails = useSelector(PackageInventoryStore.selectors.getPackageDetails);

  if (isLoading || !packageMetadata) {
    return <PackageInventoryMetadataLoading />;
  }

  const { fetcher } = Locator.parse(packageMetadata.locator);

  const plan = window.user?.organization?.stripe_plan;
  // Package label management is restricted to business and enterprise tiers
  const isPackageLabelsRestricted = isFreeTier({ plan });

  return (
    <div className="package-inventory__details__metadata">
      <Flex justify="space-between" align="flex-start">
        <Flex direction="column" align="flex-start" gap="8px" shrink="1">
          <div>
            <h1 className="package-inventory__details__metadata__title">{packageMetadata.name}</h1>
            <h2 className="package-inventory__details__metadata__subtitle">{packageMetadata.locator}</h2>
          </div>
          <PackageDetailsMetadataDescription description={packageMetadata.description} />
        </Flex>
        <Flex gap="8px">
          <PaidFeatureGate isRestricted={isPackageLabelsRestricted} hidePill>
            <Button type="secondary" size="medium" onClick={onShowManageLabelsModal}>
              Manage labels
            </Button>
          </PaidFeatureGate>
          <QualityRequired>
            <Button type="outline" size="medium" onClick={onShowBlockAllModal}>
              <span className={styles.blockText}>Block</span>
            </Button>
          </QualityRequired>
        </Flex>
      </Flex>
      <List.RowDetails>
        <List.RowDetailsItem>
          <FetcherIcon fetcher={fetcher || ''} />
          <span className="margin-left-xs">{packageMetadata.packageManager}</span>
        </List.RowDetailsItem>
        <List.RowDetailsIconItem icon={icons.folders} count={packageMetadata.numberOfProjects}>
          Projects
        </List.RowDetailsIconItem>
        <List.RowDetailsIconItem icon={icons.boxesStacked} count={packageDetails?.length ?? 0}>
          Versions
        </List.RowDetailsIconItem>
        {packageMetadata.packageLabels.length > 0 && (
          <PackageLabelsList labels={packageMetadata.packageLabels as PackageLabelAssignmentWithName[]} />
        )}
      </List.RowDetails>
    </div>
  );
};

export default PackageDetailsMetadata;
